import apiService from "@/lib/api";

const USAGE_BASE = "/api/v2/usage";

/**
 * Build the query string from **everything the caller passed**.
 *
 * `from` and `to` are optional and sent as UTC ISO strings; the window is
 * half-open `[from, to)` server-side, so passing midnight-to-midnight on
 * consecutive days never double-counts a boundary event.
 *
 * Every other key is forwarded as-is. This used to destructure `{ from, to }`
 * and build the string from those two alone, which silently swallowed
 * `include_llm_detail`: the pages built the flag correctly, the request never
 * carried it, the response never carried the engineer fields, and the toggle
 * looked broken while nothing in the chain reported an error. A pass-through
 * has no such failure mode — an unknown parameter is the server's to reject.
 *
 * Empty strings, null and undefined are dropped, so an unset control does not
 * turn into `&search=`.
 */
const queryString = (params = {}) => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, value instanceof Date ? value.toISOString() : String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

export const UsageService = {
  /**
   * Own-tenant totals: tool counts, sub-item breakdown, LLM calls and tokens.
   * Requires `usage.view`.
   */
  getSummary: async ({ from, to } = {}) => {
    try {
      // Destructured deliberately: the tenant summary route does not accept
      // `include_llm_detail` at all, so this one must not forward it.
      const response = await apiService.getSecure(`${USAGE_BASE}/summary${queryString({ from, to })}`);
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error("Error while fetching usage summary:", error);
      throw error;
    }
  },

  /**
   * Own-tenant per-user breakdown. Requires `usage.view_users`, which is a
   * SEPARATE permission from `usage.view` so a lead can be given totals without
   * seeing who did what.
   *
   * The response includes a System / candidate-initiated row and an
   * `unattributedNote` explaining why the named rows sum to less than the
   * tenant total. Render the note — without it the numbers read as a bug.
   */
  getByUser: async ({ from, to, search } = {}) => {
    try {
      const response = await apiService.getSecure(`${USAGE_BASE}/by-user${queryString({ from, to, search })}`);
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error("Error while fetching per-user usage:", error);
      throw error;
    }
  },

  /**
   * SuperAdmin: every tenant side by side.
   *
   * `params` is forwarded whole. `include_llm_detail=true` is what makes the
   * response carry model-call counts, cache hits, failures and the by-feature
   * table; without it the payload is the same shape a tenant sees.
   */
  getPlatformSummary: async (params = {}) => {
    try {
      const response = await apiService.getSecure(`${USAGE_BASE}/platform/summary${queryString(params)}`);
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error("Error while fetching platform usage:", error);
      throw error;
    }
  },

  /**
   * SuperAdmin: one tenant's summary AND its per-user breakdown, in one call.
   *
   * `params` is forwarded whole — `include_llm_detail` for the engineer view,
   * and `search` for the per-user table, which the server filters so this
   * screen and the tenant-facing one answer a search identically.
   */
  getTenantUsage: async (tenantId, params = {}) => {
    try {
      const response = await apiService.getSecure(`${USAGE_BASE}/platform/tenants/${tenantId}${queryString(params)}`);
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error("Error while fetching tenant usage:", error);
      throw error;
    }
  },
};

export default UsageService;
